import Phaser from 'phaser';
import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { MovementComponent } from '../components/MovementComponent';

/**
 * InputSystem - Captures keyboard input and translates to player movement
 * Handles WASD and arrow keys for isometric movement
 */
export class InputSystem extends System {
  public readonly requiredComponents = ['transform', 'movement'];

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
  };

  public init(): void {
    const scene = this.world.getScene();
    const keyboard = scene.input.keyboard;

    if (!keyboard) {
      console.warn('Keyboard input not available');
      return;
    }

    // Set up cursor keys
    this.cursors = keyboard.createCursorKeys();

    // Set up WASD keys
    this.wasd = {
      W: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      A: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      S: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
  }

  public update(entities: Entity[], delta: number): void {
    // Only process player entity
    const player = entities.find((e) => e.hasTag('player'));
    if (!player) return;

    const transform = player.getComponent<TransformComponent>('transform');
    const movement = player.getComponent<MovementComponent>('movement');

    if (!transform || !movement || !movement.canMove) return;

    // Get input direction
    const input = this.getInputDirection();

    if (input.x !== 0 || input.y !== 0) {
      // Apply movement in isometric direction
      movement.moveInDirection(input.x, input.y, delta);

      // Update facing direction based on movement
      const newFacing = this.getIsometricFacing(input.x, input.y);
      if (newFacing !== -1) {
        transform.facing = newFacing;
      }
    } else {
      // No input - apply friction
      movement.applyFriction(delta);
    }
  }

  /**
   * Get input direction from keyboard state
   * Returns direction in isometric world space
   */
  private getInputDirection(): { x: number; y: number } {
    let x = 0;
    let y = 0;

    const up = this.cursors?.up.isDown || this.wasd?.W.isDown;
    const down = this.cursors?.down.isDown || this.wasd?.S.isDown;
    const left = this.cursors?.left.isDown || this.wasd?.A.isDown;
    const right = this.cursors?.right.isDown || this.wasd?.D.isDown;

    // Convert screen-relative input to isometric world movement
    // In isometric view:
    // - "Up" on screen = move towards top-right of world (-y in iso)
    // - "Down" on screen = move towards bottom-left of world (+y in iso)
    // - "Left" on screen = move towards top-left of world (-x in iso)
    // - "Right" on screen = move towards bottom-right of world (+x in iso)

    if (up) {
      // Move "up" on screen = decrease both X and Y in world
      x -= 1;
      y -= 1;
    }
    if (down) {
      // Move "down" on screen = increase both X and Y in world
      x += 1;
      y += 1;
    }
    if (left) {
      // Move "left" on screen = decrease X, increase Y in world
      x -= 1;
      y += 1;
    }
    if (right) {
      // Move "right" on screen = increase X, decrease Y in world
      x += 1;
      y -= 1;
    }

    // Normalize diagonal movement
    if (x !== 0 && y !== 0) {
      const length = Math.sqrt(x * x + y * y);
      x /= length;
      y /= length;
    }

    return { x, y };
  }

  /**
   * Get facing direction (0-7) from input direction
   * Maps isometric movement to 8-direction facing
   */
  private getIsometricFacing(dx: number, dy: number): number {
    if (dx === 0 && dy === 0) return -1;

    // Use atan2 to get angle, then map to 8 directions
    const angle = Math.atan2(dy, dx);
    // Convert to 0-7 range (0 = right, going clockwise)
    let direction = Math.round(((angle + Math.PI) / (2 * Math.PI)) * 8) % 8;

    return direction;
  }

  public destroy(): void {
    // Keyboard keys are managed by the scene, no cleanup needed
  }
}
